'use client';

import React, { useContext, createContext, useState, useEffect } from 'react';
import { getSiteConfig } from '../../siteConfig';
import { useBookingConfiguration } from '../hooks/useBookingConfiguration';
import { useTranslation } from 'react-i18next';
import { combinePeriodsOnWeek, getWeek } from '../utils/dateTime';
import { getCachedItem, setCachedItem } from '../utils/cacheUtil';

const BookingContext = createContext({});

const BookingContextProvider = ({
  initSeason,
  initDestination,
  initHotel,
  children,
  initiallyVisible,
}) => {
  const { ethelMarket: market } = getSiteConfig();

  const { content } = useBookingConfiguration();
  const confContent = JSON.parse(content);

  const [visible, setVisible] = useState(null);
  const [isVisibleOnMobile, setIsVisibleOnMobile] = useState(false);
  const [isVisibleOnDesktop, setIsVisibleOnDesktop] = useState(null);
  const [season, setSeason] = useState(
    initSeason ? initSeason.toLowerCase() : 'winter',
  );

  const travelTypes = confContent.travel_types;
  const [travelType, setTravelType] = useState('Charter');

  //TODO:set charter types from storyblok config / page override

  const { t } = useTranslation(['booking']);

  const charterTypes = confContent.charter_types.map((ct) => {
    return { id: ct.toLowerCase(), name: t(`charterTypes.${ct}`) };
  });
  const [charterType, setCharterType] = useState('winter');
  const [charterTypeId, setCharterTypeId] = useState('');

  const getPreferredAirport = () => {
    return getCachedItem('preferred-airport');
  };

  const [airports, setAirports] = useState();
  const [airport, setAirport] = useState(getPreferredAirport());
  const [airportObject, setAirportObject] = useState();
  const [destinations, setDestinations] = useState();
  /* TODO: enable for search 2 */
  const [destination, setDestination] = useState(initDestination || '*');
  //const [destination, setDestination] = useState(initDestination);

  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState();
  const [departureDate, setDepartureDate] = useState();
  const [quickbookData, setQuickbookData] = useState();
  const [isFullscreenModal, setIsFullscreenModal] = useState(false);
  const groupInfoBlock = confContent.group_info;

  const getRooms = () => {
    let rooms = getCachedItem('search-rooms');
    if (rooms) {
      return rooms;
    } else {
      return [{ adults: 2, children: 0, childrenAges: [] }];
    }
  };

  //TODO: init from local storage if exists
  const [rooms, setRooms] = useState(getRooms());
  const [hotel, setHotel] = useState(initHotel);

  useEffect(() => {
    setCachedItem('search-rooms', rooms);
  }, [rooms]);

  useEffect(() => {
    if (airport) {
      setCachedItem('preferred-airport', airport);
    }
  }, [airport]);

  useEffect(() => {
    setIsVisibleOnMobile(visible === true);
    setIsVisibleOnDesktop(
      (initiallyVisible === true && visible === null) || visible === true,
    );
  }, [visible, initiallyVisible]);

  //load data when chartertype, traveltype, season or market changes
  useEffect(() => {
    const fetchTravelData = async (market, travelType, season) => {
      const cacheKey = `travelData-${market}-${travelType}-${season}`;

      let cachedData = getCachedItem(cacheKey);

      if (cachedData) {
        setQuickbookData(cachedData);
      } else {
        const response = await fetch(
          `https://api-web.alpresor.se/v1/query/travelBooking/quikbook?marketId=${market}&travelType=${travelType}&EpiServerId=${season}`,
        );
        const res = await response.json();

        setCachedItem(cacheKey, res.data);
        setQuickbookData(res.data);
      }
    };
    fetchTravelData(market, travelType, season);
  }, [charterType, market, season, travelType]);

  //set initial data when qb data changes
  useEffect(() => {
    //console.log('quickbookData', quickbookData);
    if (quickbookData) {
      setCharterTypeId(quickbookData.id);

      setAirports(quickbookData.airports);
    }
  }, [quickbookData]);

  //set destinations when airport changes
  useEffect(() => {
    if (airports && airports.length > 0) {
      if (airport) {
        const airp = airports.find((a) => a.airportId === airport);
        if (airp) {
          setDestinations(airp.destinationGroups);
          setAirportObject(airp);
        } else {
          setDestinations(airports[0].destinationGroups);
          setAirportObject(airports[0]);
          setAirport(airports[0].airportId);
        }
      } else {
        setDestinations(airports[0].destinationGroups);
        setAirportObject(airports[0]);
        setAirport(airports[0].airportId);
      }
    }
  }, [airport, airports]);

  //set periods when destination changes
  useEffect(() => {
    if (destination && airportObject) {
      let periods = airportObject.periods;

      if (destination !== '*') {
        const destinationGroup = destinations.find(
          (group) =>
            group.id === destination ||
            group.destinations.some((d) => d.id === destination),
        );

        if (!destinationGroup) {
          setDestination(airportObject.destinationGroups[0].destinations[0].id);
        }

        if (destinationGroup?.id === destination) {
          periods = destinationGroup.periods;
        } else {
          if (destinationGroup && destinationGroup.destinations) {
            const destinationInGroup = destinationGroup.destinations.find(
              (d) => d.id === destination,
            );
            periods = destinationInGroup.periods;
          }
        }
      }

      const combinedPeriods =
        season === 'christmas' ? periods : combinePeriodsOnWeek(periods);
      const sortedPeriods = combinedPeriods.sort(
        (a, b) => new Date(a.out) - new Date(b.out),
      );
      setPeriods(sortedPeriods);
    } else {
      if (airportObject) {
        setDestination(airportObject.destinationGroups[0].destinations[0].id);
      }
    }
  }, [destination, destinations, airportObject, season]);

  //set period when periods change
  useEffect(() => {
    if (periods?.length > 0 && !period) {
      setPeriod(periods[0]);
    } else if (periods?.length > 0 && period) {
      const selectedPeriod = periods.find((p) => p.out === period.out);
      if (!selectedPeriod) {
        setPeriod(periods[0]);
      }
    }
  }, [periods, period]);

  useEffect(() => {
    if (departureDate && periods?.length > 0) {
      const matchingPeriod = periods.find(
        (p) => p.out.substr(0, 10) === departureDate.substr(0, 10),
      );
      if (matchingPeriod) {
        setPeriod(matchingPeriod);
      }
    }
  }, [departureDate, periods]);

  return (
    <BookingContext.Provider
      value={{
        visible,
        setVisible,
        isVisibleOnMobile,
        isVisibleOnDesktop,
        isFullscreenModal,
        setIsFullscreenModal,
        season,
        setSeason,
        travelTypes,
        travelType,
        setTravelType,
        charterTypes,
        charterType,
        setCharterType,
        charterTypeId,
        setCharterTypeId,
        periods,
        setPeriods,
        period,
        setPeriod,
        setDepartureDate,
        destinations,
        airports,
        airport,
        setAirport,
        destination,
        setDestination,
        hotel,
        setHotel,
        rooms,
        setRooms,
        groupInfoBlock,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

const useBookingContext = () => useContext(BookingContext);

export { BookingContextProvider, useBookingContext };
